import { Form, Formik } from 'formik';
import styled from 'styled-components';
import { object, ref, string } from 'yup';
import { Backdrop, DialogConfirmation } from '@spotify-internal/encore-web';
import { useT } from '@spotify-internal/i18n-core';
import { sendEvent } from '@spotify-internal/s4mp-page-analytics';
import { parseURI, URITypeMap } from '@spotify-internal/uri';
import { useEnrichard } from '@hooks/useEnrichard';
import { useRelocationFormEmail } from '@hooks/useRelocationFormEmail';
import { FETCH_STATUS } from '@hooks/useSWRFetch/types';
import { EVENT_ACTION, EVENT_CATEGORY } from '@lib/ga/types';
import { GA_PRODUCT_CATALOG } from '@helpers/constants';
import { albumUriValidationRegex } from '@helpers/regexPatterns';
import { FooterButtons } from './FooterButtons';
import { FormBody } from './FormBody';
import { getAlbumUris, getURI, validComment } from './helpers';
import { FORM_CASES, FormValues } from './types';

const StyledDialogConfirmation = styled(DialogConfirmation)`
  min-width: 1000px;
`;

// Case values are required by enrichard endpoint
const getCaseValue = (caseType: FORM_CASES) => {
  switch (caseType) {
    case FORM_CASES.ANOTHER_ARTIST_ON_MY_PAGE:
      return "Another artist's music is on my profile.";
    case FORM_CASES.MY_RELEASE_ON_ANOTHER_PAGE:
      return "Some of my music is on another artist's profile.";
    case FORM_CASES.MOVE_MY_RELEASE:
      return 'I need a release moved to a new artist page.';
    default:
      return 'Case unknown';
  }
};

const getArtistUri = (caseType: FORM_CASES, formValues: FormValues) => {
  switch (caseType) {
    case FORM_CASES.MY_RELEASE_ON_ANOTHER_PAGE:
      return formValues.destinationArtistURI;
    case FORM_CASES.ANOTHER_ARTIST_ON_MY_PAGE:
      return formValues.myArtistURI;
    case FORM_CASES.MOVE_MY_RELEASE:
      return formValues.currentArtistURI;
    default:
      return '';
  }
};

interface ArtistRelocationModalProps {
  firstBtnRef: React.RefObject<HTMLElement | null>;
  lastBtnRef: React.RefObject<HTMLElement | null>;
  onClose: () => void;
  uri: string;
  additionalComment?: string;
}

export const ArtistRelocationModal = ({
  firstBtnRef,
  lastBtnRef,
  onClose,
  uri,
  additionalComment,
}: ArtistRelocationModalProps) => {
  const t = useT();
  const {
    actions: { postToEnrichard },
    status,
  } = useEnrichard();
  const isLoading = status === FETCH_STATUS.FETCHING;
  const isError = status === FETCH_STATUS.ERROR;
  const isFetched = status === FETCH_STATUS.SUCCESS;

  const {
    data: { savedEmail },
    actions: { saveEmail },
  } = useRelocationFormEmail();

  const handleSubmit = async (formValues: FormValues) => {
    const caseType = formValues.case;

    sendEvent({
      eventCategory: EVENT_CATEGORY.CONTENT_RELOCATION_FORM,
      eventAction: EVENT_ACTION.CLICK,
      eventLabel: 'Content relocation form submitted',
      product: GA_PRODUCT_CATALOG,
    });
    const caseEnumValue = getCaseValue(caseType);
    const destinationArtist =
      formValues.case === FORM_CASES.MY_RELEASE_ON_ANOTHER_PAGE
        ? getURI(formValues.incorrectArtistURI)
        : '';

    const albumUris = getAlbumUris(formValues.albumURIs);
    const artistUri = getArtistUri(caseType, formValues);
    const comments = additionalComment
      ? `${additionalComment}\n----\n${formValues.comments}`
      : formValues.comments;

    const body = JSON.stringify({
      email: formValues.email,
      problem: caseEnumValue,
      date: new Date().toUTCString(),
      artist_uri: artistUri,
      other_artist_uri: destinationArtist,
      release_uris: albumUris,
      artist_name:
        caseType === FORM_CASES.MOVE_MY_RELEASE ? formValues.artistName : null,
      comments: comments,
    });

    await postToEnrichard(body).then(res => {
      if (!res) {
        return;
      }
      saveEmail(formValues.email);
    });
  };

  // Validation
  const validArtistURIMessage = t(
    'atlas.relocation.validation.artist_uri',
    `Please enter a valid Spotify Artist URI. 'spotify:artist:uri-id'`,
    'This message appears when the user uses an invalid Spotify artist URI',
  );
  const sameSourceAndDestinationArtistURIErrorMessage = t(
    'atlas.relocation.validation.source_and_destination_artist_uri',
    'Source and Destination Artist URIs should not be the same',
    'This message appears when the source and destination artist URI are the same',
  );

  // Required
  const correctArtistURIErrorMessage = t(
    'atlas.relocation.required.correct_artist_URI',
    'Please enter the Correct Artist URI to where your music should be relocated',
    'This message appears when the user leaves the correct artist URI field blank',
  );

  const validationSchema = object({
    email: string()
      .email(
        t(
          'atlas.relocation.validation.valid_email',
          'A valid email address is required',
          'This message appears when the user uses an invalid email address',
        ),
      )
      .required(
        t(
          'atlas.relocation.required.valid_email',
          'Email address is required',
          'This message appears when the user leaves the email address field blank',
        ),
      ),
    case: string()
      .oneOf([
        FORM_CASES.MY_RELEASE_ON_ANOTHER_PAGE,
        FORM_CASES.ANOTHER_ARTIST_ON_MY_PAGE,
        FORM_CASES.MOVE_MY_RELEASE,
      ])
      .required(
        t(
          'atlas.relocation.required.select_one_option',
          'Please select one of the available options.',
          'This message appears when the user does not select a case',
        ),
      ),
    destinationArtistURI: string().when('case', {
      is: FORM_CASES.MY_RELEASE_ON_ANOTHER_PAGE,
      then: string()
        .required(correctArtistURIErrorMessage)
        .test(
          'isArtistUri',
          validArtistURIMessage,
          value => parseURI(value)?.type === URITypeMap.ARTIST,
        )
        .notOneOf(
          [ref('incorrectArtistURI')],
          sameSourceAndDestinationArtistURIErrorMessage,
        ),
      otherwise: string().notRequired(),
    }),
    incorrectArtistURI: string().when('case', {
      is: FORM_CASES.MY_RELEASE_ON_ANOTHER_PAGE,
      then: string()
        .required(
          t(
            'atlas.relocation.required.incorrect_artist_URI',
            'Please enter the Incorrect Artist URI which your music is currently located at',
            'This message appears when the user leaves the incorrect artist URI field empty',
          ),
        )
        .notOneOf(
          [ref('destinationArtistURI')],
          sameSourceAndDestinationArtistURIErrorMessage,
        )
        .test(
          'isArtistUri',
          validArtistURIMessage,
          value => parseURI(value)?.type === URITypeMap.ARTIST,
        ),
    }),
    myArtistURI: string().when('case', {
      is: FORM_CASES.ANOTHER_ARTIST_ON_MY_PAGE,
      then: string()
        .required(correctArtistURIErrorMessage)
        .test(
          'isArtistUri',
          validArtistURIMessage,
          value => parseURI(value)?.type === URITypeMap.ARTIST,
        ),
    }),
    currentArtistURI: string().when('case', {
      is: FORM_CASES.MOVE_MY_RELEASE,
      then: string()
        .required(correctArtistURIErrorMessage)
        .test(
          'isArtistUri',
          validArtistURIMessage,
          value => parseURI(value)?.type === URITypeMap.ARTIST,
        ),
    }),
    artistName: string().when('case', {
      is: FORM_CASES.MOVE_MY_RELEASE,
      then: string().required(
        t(
          'atlas.relocation.required.artist_name',
          'Please enter the name of the new Artist Page to where your music should be relocated',
          'This message appears when the user leaves the artist name field empty',
        ),
      ),
    }),
    albumURIs: string()
      .required(
        t(
          'atlas.relocation.required.album_uris',
          'Please enter the Spotify Album URI of the affected album',
          'This message appears when the user leaves the album URIs field empty',
        ),
      )
      .matches(
        albumUriValidationRegex,
        t(
          'atlas.relocation.validation.album_uri',
          'Please enter a valid Spotify Album URI. For multiple Spotify Album URI submissions, please separate each with a comma, space or semicolon',
          'This message appears when the Spotify album URI (singular) is invalid',
        ),
      )
      .test(
        'isAlbumUri',
        t(
          'atlas.relocation.validation.album_uris',
          'Please enter valid Spotify Album URIs. For multiple Spotify Album URI submissions, please separate each with a comma, space or semicolon',
          'This message appears when the Spotify album URIs (plural) is invalid',
        ),
        value =>
          value
            ?.split(/,| |\n|;/)
            .map(album => album.trim())
            .filter(album => album.length > 0)
            .filter(album => parseURI(album)?.type === URITypeMap.ALBUM)
            .length ===
          value?.split(/,| |;/).filter(album => album.length > 0).length,
      ),
    comments: string()
      .notRequired()
      .max(
        150,
        t(
          'atlas.relocation.validation.exceeds_150_chars',
          'Please enter no more than 150 characters',
          'This message appears when the text input exceeds 150 characters',
        ),
      )
      .test(
        'noUrisInValue',
        t(
          'atlas.relocation.validation.no_uris',
          'Please do not enter URIs here',
          'This message appears if URIs appear in this field',
        ),
        comments => validComment(comments),
      ),
  });

  return (
    <Backdrop center onClose={onClose}>
      <Formik
        initialValues={{
          email: savedEmail ?? '',
          albumURIs: uri,
          artistName: '',
          comments: '',
          case: FORM_CASES.MY_RELEASE_ON_ANOTHER_PAGE,
          // First form
          destinationArtistURI: '',
          incorrectArtistURI: '',
          // Second form
          myArtistURI: '',
          // Third form
          currentArtistURI: '',
        }}
        validationSchema={validationSchema}
        onSubmit={(values, {}) => handleSubmit(values)}
      >
        {({
          values,
          handleChange,
          errors,
          touched,
          handleBlur,
          isSubmitting,
        }) => (
          <Form>
            <StyledDialogConfirmation
              id="artist-relocation-modal"
              dialogTitle={t(
                'atlas.relocation.content_relocation_form',
                'Content Relocation Form',
                'Title for form to relocate content',
              )}
              body={
                <FormBody
                  firstBtnRef={firstBtnRef}
                  status={status}
                  values={values}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  errors={errors}
                  touched={touched}
                  disableEmailInput={!!savedEmail}
                />
              }
              footer={
                <FooterButtons
                  lastBtnRef={lastBtnRef}
                  isError={isError}
                  isFetched={isFetched}
                  submitButtonDisabled={isSubmitting || isLoading}
                  onClose={onClose}
                />
              }
            />
          </Form>
        )}
      </Formik>
    </Backdrop>
  );
};
